<template>
    <b-row>
        <b-col cols="12">
            <b-card>
                <header class="d-md-flex justify-content-md-between align-items-center">
                    <h5>Configuración de cambio de butaca</h5>
                </header>
                <hr/>
                <b-card-text>
                    <b-row>
                        <b-col v-if="loading" cols="12" class="text-center py-5">
                            <b-spinner variant="primary"/>
                            <p class="mt-2">Cargando configuración...</p>
                        </b-col>

                        <b-col v-if="error && !loading" cols="12" class="mb-3">
                            <b-alert show variant="danger">{{ error }}</b-alert>
                        </b-col>

                        <template v-if="!loading">
                            <!-- SGA mode: only the on/off toggle for seat change -->
                            <template v-if="hasSga">
                                <b-col cols="12" class="mb-3">
                                    <p class="text-muted small mb-3">
                                        <b-icon icon="info-circle" class="mr-1" />
                                        Este teatro tiene integración con SGA. Las opciones del cambio de asiento se configuran en el panel de SGA.
                                    </p>
                                </b-col>
                                <b-col cols="12" class="mb-3">
                                    <b-form-checkbox v-model="seatChangeActive">
                                        Permitir cambio de butaca
                                    </b-form-checkbox>
                                </b-col>
                            </template>

                            <!-- Standard mode: show all existing options -->
                            <template v-else>
                                <b-col cols="12" class="mb-3">
                                    <b-form-checkbox v-model="seatChangeActive">
                                        Permitir cambio de butaca
                                    </b-form-checkbox>
                                </b-col>

                                <b-col cols="12" md="6" class="mb-3">
                                    <b-form-group label="Fecha inicio">
                                        <b-form-input
                                            v-model="seatChangeStart"
                                            type="date"
                                            :disabled="!seatChangeActive"
                                        />
                                    </b-form-group>
                                </b-col>

                                <b-col cols="12" md="6" class="mb-3">
                                    <b-form-group label="Fecha fin">
                                        <b-form-input
                                            v-model="seatChangeEnd"
                                            type="date"
                                            :disabled="!seatChangeActive"
                                        />
                                    </b-form-group>
                                </b-col>

                                <b-col cols="12" md="6" class="mb-3">
                                    <b-form-group label="Tipos de abono elegibles">
                                        <multiselect
                                            v-model="selectedTiposAbono"
                                            placeholder="Seleccionar tipos de abono"
                                            label="descripcion"
                                            track-by="id"
                                            :options="tiposAbonoOptions"
                                            :multiple="true"
                                            :disabled="!seatChangeActive"
                                        />
                                    </b-form-group>
                                </b-col>

                                <b-col cols="12" md="6" class="mb-3">
                                    <b-form-group label="Precios elegibles">
                                        <multiselect
                                            v-model="selectedDescuentos"
                                            placeholder="Seleccionar descuentos"
                                            label="label"
                                            track-by="id"
                                            :options="descuentosOptions"
                                            :multiple="true"
                                            :disabled="!seatChangeActive"
                                        />
                                    </b-form-group>
                                </b-col>
                            </template>
                        </template>
                    </b-row>
                    <hr/>
                    <div v-if="!loading" class="row">
                        <div class="col-12 d-flex justify-content-end">
                            <button class="button" :disabled="saving" @click="saveConfig">
                                <b-spinner v-if="saving" small class="mr-2"/>
                                Guardar
                            </button>
                        </div>
                    </div>
                </b-card-text>
            </b-card>
        </b-col>
    </b-row>
</template>

<script setup>
    import { ref, onMounted, computed } from 'vue';
    import store from '@/store';
    import { config } from '@/config.js';
    import { useToast } from '@/composables/common/useToast';

    const toast = useToast();

    const listaId = computed(() => store.getters.getListaId);
    const teatro = computed(() => store.getters.getTeatro || {});
    const hasSga = computed(() => Number(teatro.value?.sga) === 1);

    const loading = ref(false);
    const saving = ref(false);
    const error = ref(null);

    const seatChangeActive = ref(false);
    const seatChangeStart = ref('');
    const seatChangeEnd = ref('');
    const canChangeSeat = ref(false);

    const tiposAbonoOptions = ref([]);
    const descuentosOptions = ref([]);
    const selectedTiposAbono = ref([]);
    const selectedDescuentos = ref([]);

    // Fetch precios (tipos + nested descuentos) from back.precios.index
    const loadOptions = () => {
        return store.dispatch(
            config.getVuexAlias('back.precios.index'),
            { filters: { all: 1 }, lista_id: listaId.value }
        )
            .then(res => {
                // Response is a plain array with ?all=1, or wrapped in { data: [...] }
                const precios = Array.isArray(res.data)
                    ? res.data
                    : (res.data && Array.isArray(res.data.data) ? res.data.data : []);

                // Tipos de abono = top-level precios
                tiposAbonoOptions.value = precios.map(p => ({
                    id: p.id,
                    descripcion: p.descripcion || '',
                }));

                // Descuentos = flatten nested descuentos from all precios
                const allDescuentos = [];
                precios.forEach(p => {
                    if (p.descuentos && Array.isArray(p.descuentos)) {
                        p.descuentos.forEach(d => {
                            allDescuentos.push({
                                id: d.id,
                                label: d.label || d.descripcion || '',
                                descripcion: d.descripcion || '',
                                precio_id: p.id,
                            });
                        });
                    }
                });
                descuentosOptions.value = allDescuentos;
            })
            .catch(err => {
                console.error('[TabCambioButaca] Error loading precios:', err);
            });
    };

    const loadConfig = () => {
        loading.value = true;
        error.value = null;

        // SGA theaters only need the renewal prices toggle, skip precios loading
        const configPromise = hasSga.value
            ? Promise.resolve()
            : loadOptions();

        configPromise
            .then(() => store.dispatch(
                config.getVuexAlias('back.abonados.lista.configuracion-cambio-butaca'),
                { lista_id: listaId.value }
            ))
            .then(res => {
                const data = res.data;
                seatChangeActive.value = !!data.seat_change_active;
                seatChangeStart.value = data.seat_change_start
                    ? data.seat_change_start.substring(0, 10) : '';
                seatChangeEnd.value = data.seat_change_end
                    ? data.seat_change_end.substring(0, 10) : '';
                canChangeSeat.value = !!data.can_change_seat;

                const eligibleTypes = data.seat_change_eligible_types || [];
                selectedTiposAbono.value = tiposAbonoOptions.value.filter(
                    t => eligibleTypes.includes(t.id)
                );

                const eligibleDescuentos = data.seat_change_eligible_descuentos || [];
                selectedDescuentos.value = descuentosOptions.value.filter(
                    d => eligibleDescuentos.includes(d.id)
                );
            })
            .catch(err => {
                console.error('[TabCambioButaca] Error loading config:', err);
            })
            .finally(() => {
                loading.value = false;
            });
    };

    const saveConfig = () => {
        if (!hasSga.value && seatChangeStart.value && seatChangeEnd.value && seatChangeStart.value > seatChangeEnd.value) {
            toast.error('Fecha inicio debe ser anterior a fecha fin.');
            return;
        }

        saving.value = true;
        error.value = null;

        const payload = {
            lista_id: listaId.value,
            seat_change_active: seatChangeActive.value,
        };

        // In SGA mode, only the active flag is sent.
        // In standard mode, also send the rest of the config.
        if (!hasSga.value) {
            payload.seat_change_start = seatChangeStart.value || null;
            payload.seat_change_end = seatChangeEnd.value || null;
            payload.seat_change_eligible_types = selectedTiposAbono.value.map(t => t.id);
            payload.seat_change_eligible_descuentos = selectedDescuentos.value.map(d => d.id);
        }

        store.dispatch(
            config.getVuexAlias('back.abonados.lista.configuracion-cambio-butaca.update'),
            payload
        )
            .then(() => {
                toast.success('Configuración guardada correctamente.');
            })
            .catch(err => {
                console.error('[TabCambioButaca] Error saving config:', err);
                const msg = err && err.data && err.data.message
                    ? err.data.message
                    : (err && err.message ? err.message : 'Error al guardar la configuración.');
                error.value = msg;
            })
            .finally(() => {
                saving.value = false;
            });
    };

    onMounted(() => {
        loadConfig();
    });
</script>
